/* ==========================================================================
   GLOWSTONE — Image Cropper
   Premium drag/zoom/rotate crop with rubber-band physics.
   ========================================================================== */
(function () {
  'use strict';

  var DEG = Math.PI / 180;
  var MAX_ZOOM = 3;
  var RB_K = 0.35;
  var SNAP_MS = 300;
  var MIN_PPI = 150;

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  function rubber(val, limit, dim) {
    if (Math.abs(val) <= limit) return val;
    var s = val > 0 ? 1 : -1;
    var o = Math.abs(val) - limit;
    return s * (limit + dim * (1 - 1 / (o / (dim * RB_K) + 1)));
  }

  function ease(t) { return 1 - (1 - t) * (1 - t) * (1 - t); }

  function rotBounds(w, h, deg) {
    var r = Math.abs(deg) * DEG;
    return [Math.abs(w * Math.cos(r)) + Math.abs(h * Math.sin(r)),
            Math.abs(w * Math.sin(r)) + Math.abs(h * Math.cos(r))];
  }

  function tDist(a, b) {
    var dx = a.clientX - b.clientX, dy = a.clientY - b.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function tMid(a, b) {
    return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
  }

  function parseRatio(str) {
    if (!str) return 1;
    var p = str.split('/');
    return p.length === 2 ? parseFloat(p[0]) / parseFloat(p[1]) : 1;
  }

  function parseDims(label) {
    var m = label && label.match(/(\d+)\s*[×x]\s*(\d+)/i);
    return m ? [parseFloat(m[1]), parseFloat(m[2])] : [9, 9];
  }

  /* ---------------------------------------------------------------------- */

  function Cropper(el) {
    this.el = el;
    this.vp = el.querySelector('[data-gs-crop-viewport]');
    this.img = el.querySelector('[data-gs-crop-image]');
    this.frame = el.querySelector('[data-gs-crop-frame]');
    this.warn = el.querySelector('[data-gs-crop-warning]');
    this.scaleSlider = el.querySelector('[data-gs-crop-scale]');
    this.scaleVal = el.querySelector('[data-gs-crop-scale-value]');
    this.rotSlider = el.querySelector('[data-gs-crop-rotate]');
    this.rotVal = el.querySelector('[data-gs-crop-rotate-value]');

    this.iw = 0; this.ih = 0;
    this.fw = 0; this.fh = 0;
    this.ratio = 1;
    this.printW = 9; this.printH = 9;

    this.px = 0; this.py = 0;
    this.zoom = 1;
    this.rot = 0;
    this.q = 0;
    this.fh_ = false; this.fv_ = false;

    this._drag = null; this._pinch = null; this._anim = null;
    this._init();
  }

  var P = Cropper.prototype;

  P._init = function () {
    var self = this;
    this._bindPointer();
    this._bindTouch();
    this._bindWheel();
    this._bindKeys();
    this._bindControls();

    this._ro = new ResizeObserver(function () {
      self._layout(); self._clamp(); self._render();
    });
    this._ro.observe(this.vp);

    this.img.addEventListener('dragstart', function (e) { e.preventDefault(); });
    this.vp.addEventListener('contextmenu', function (e) { e.preventDefault(); });
  };

  /* ---- Image ---- */

  P.setImage = function (url) {
    var self = this;
    var i = new Image();
    i.onload = function () {
      self.iw = i.naturalWidth; self.ih = i.naturalHeight;
      self.img.src = url; self.img.hidden = false;
      self.el.hidden = false;
      requestAnimationFrame(function () { self.reset(true); });
    };
    i.src = url;
  };

  P.removeImage = function () {
    this.el.hidden = true;
    this.img.removeAttribute('src'); this.img.hidden = true;
    this._stop();
  };

  /* ---- Layout ---- */

  P.setRatio = function (ratioStr, sizeLabel) {
    this.ratio = parseRatio(ratioStr);
    var d = parseDims(sizeLabel);
    this.printW = d[0]; this.printH = d[1];
    this._layout();
    if (this.zoom < 1) this.zoom = 1;
    var l = this._lim();
    this._animTo(clamp(this.px, -l.x, l.x), clamp(this.py, -l.y, l.y), this.zoom);
    this._ui(); this._res();
  };

  P._layout = function () {
    if (!this.vp) return;
    var vw = this.vp.clientWidth, vh = this.vp.clientHeight, m = 0.88;
    if (vw / vh > this.ratio) {
      this.fh = Math.round(vh * m); this.fw = Math.round(this.fh * this.ratio);
    } else {
      this.fw = Math.round(vw * m); this.fh = Math.round(this.fw / this.ratio);
    }
    if (this.frame) {
      this.frame.style.width = this.fw + 'px';
      this.frame.style.height = this.fh + 'px';
    }
  };

  /* ---- Scale math ---- */

  P._totalR = function () { return this.rot + this.q * 90; };

  P._minDS = function () {
    if (!this.iw) return 1;
    var b = rotBounds(this.iw, this.ih, this._totalR());
    return Math.max(this.fw / b[0], this.fh / b[1]);
  };

  P._ds = function () { return this._minDS() * this.zoom; };

  P._lim = function () {
    var ds = this._ds();
    var b = rotBounds(this.iw * ds, this.ih * ds, this._totalR());
    return { x: Math.max(0, (b[0] - this.fw) / 2), y: Math.max(0, (b[1] - this.fh) / 2) };
  };

  P._clamp = function () {
    var l = this._lim();
    this.px = clamp(this.px, -l.x, l.x);
    this.py = clamp(this.py, -l.y, l.y);
  };

  /* ---- Render ---- */

  P._render = function () {
    if (!this.iw) return;
    var ds = this._ds(), r = this._totalR();
    var vw = this.vp.clientWidth, vh = this.vp.clientHeight;
    var tx = vw / 2 + this.px, ty = vh / 2 + this.py;
    var t = 'translate(' + tx + 'px,' + ty + 'px)';
    if (this.fh_) t += ' scaleX(-1)';
    if (this.fv_) t += ' scaleY(-1)';
    t += ' rotate(' + r + 'deg) scale(' + ds + ') translate(' + (-this.iw / 2) + 'px,' + (-this.ih / 2) + 'px)';
    this.img.style.transform = t;
  };

  /* ---- Pointer ---- */

  P._bindPointer = function () {
    var self = this;
    this.vp.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch' && self._pinch) return;
      if (!self.iw) return;
      e.preventDefault();
      self._stop();
      self._drag = { id: e.pointerId, sx: e.clientX, sy: e.clientY, px0: self.px, py0: self.py };
      self.vp.setPointerCapture(e.pointerId);
      self.vp.classList.add('is-grabbing');
    });

    this.vp.addEventListener('pointermove', function (e) {
      if (!self._drag || self._drag.id !== e.pointerId) return;
      e.preventDefault();
      var rx = self._drag.px0 + (e.clientX - self._drag.sx);
      var ry = self._drag.py0 + (e.clientY - self._drag.sy);
      var l = self._lim(), dim = Math.max(self.fw, self.fh);
      self.px = rubber(rx, l.x, dim);
      self.py = rubber(ry, l.y, dim);
      self._render();
    });

    var up = function (e) {
      if (!self._drag || self._drag.id !== e.pointerId) return;
      self._drag = null;
      self.vp.classList.remove('is-grabbing');
      self._snap();
    };
    this.vp.addEventListener('pointerup', up);
    this.vp.addEventListener('pointercancel', up);
  };

  /* ---- Touch (pinch) ---- */

  P._bindTouch = function () {
    var self = this;
    this.vp.addEventListener('touchstart', function (e) {
      if (e.touches.length === 2) {
        e.preventDefault(); self._stop(); self._drag = null;
        self._pinch = {
          d0: tDist(e.touches[0], e.touches[1]),
          z0: self.zoom,
          m0: tMid(e.touches[0], e.touches[1]),
          px0: self.px, py0: self.py
        };
      }
    }, { passive: false });

    this.vp.addEventListener('touchmove', function (e) {
      if (!self._pinch || e.touches.length !== 2) return;
      e.preventDefault();
      var d = tDist(e.touches[0], e.touches[1]);
      var m = tMid(e.touches[0], e.touches[1]);
      self.zoom = clamp(self._pinch.z0 * (d / self._pinch.d0), 1, MAX_ZOOM);
      self.px = self._pinch.px0 + (m.x - self._pinch.m0.x);
      self.py = self._pinch.py0 + (m.y - self._pinch.m0.y);
      var l = self._lim(), dim = Math.max(self.fw, self.fh);
      self.px = rubber(self.px, l.x, dim);
      self.py = rubber(self.py, l.y, dim);
      self._render(); self._ui();
    }, { passive: false });

    var end = function () { if (self._pinch) { self._pinch = null; self._snap(); } };
    this.vp.addEventListener('touchend', end);
    this.vp.addEventListener('touchcancel', end);
  };

  /* ---- Wheel ---- */

  P._bindWheel = function () {
    var self = this;
    this.vp.addEventListener('wheel', function (e) {
      if (!self.iw) return;
      e.preventDefault(); self._stop();
      var d = -e.deltaY * (e.deltaMode === 1 ? 0.03 : 0.001);
      self.zoom = clamp(self.zoom + d, 1, MAX_ZOOM);
      self._clamp(); self._render(); self._ui(); self._res();
    }, { passive: false });
  };

  /* ---- Keyboard ---- */

  P._bindKeys = function () {
    var self = this;
    this.vp.addEventListener('keydown', function (e) {
      if (!self.iw) return;
      var s = 4, ok = true;
      if (e.key === 'ArrowLeft') self.px -= s;
      else if (e.key === 'ArrowRight') self.px += s;
      else if (e.key === 'ArrowUp') self.py -= s;
      else if (e.key === 'ArrowDown') self.py += s;
      else ok = false;
      if (ok) { e.preventDefault(); self._clamp(); self._render(); }
    });
  };

  /* ---- Controls ---- */

  P._bindControls = function () {
    var self = this;

    if (this.scaleSlider) {
      this.scaleSlider.min = '1'; this.scaleSlider.max = String(MAX_ZOOM); this.scaleSlider.step = '0.01';
      this.scaleSlider.addEventListener('input', function () {
        self._stop(); self.zoom = parseFloat(this.value);
        self._clamp(); self._render(); self._uiScale(); self._res();
      });
    }

    if (this.rotSlider) {
      this.rotSlider.addEventListener('input', function () {
        self._stop(); self.rot = parseFloat(this.value);
        self._clamp(); self._render(); self._uiRot(); self._res();
      });
    }

    this._on('[data-gs-crop-ccw]', function () { self.q = (self.q + 3) % 4; self._afterRot(); });
    this._on('[data-gs-crop-cw]', function () { self.q = (self.q + 1) % 4; self._afterRot(); });

    this._on('[data-gs-crop-flip-h]', function () {
      self.fh_ = !self.fh_; this.setAttribute('aria-pressed', String(self.fh_)); self._render();
    });
    this._on('[data-gs-crop-flip-v]', function () {
      self.fv_ = !self.fv_; this.setAttribute('aria-pressed', String(self.fv_)); self._render();
    });

    this._on('[data-gs-crop-fill]', function () { self._stop(); self._animTo(0, 0, 1); });
    this._on('[data-gs-crop-reset]', function () { self.reset(false); });
  };

  P._on = function (sel, fn) {
    this.el.querySelectorAll(sel).forEach(function (el) { el.addEventListener('click', fn); });
  };

  P._afterRot = function () { this._clamp(); this._render(); this._ui(); this._res(); };

  P._ui = function () {
    this._uiScale(); this._uiRot();
    if (this.scaleSlider) this.scaleSlider.value = this.zoom;
    if (this.rotSlider) this.rotSlider.value = this.rot;
  };

  P._uiScale = function () {
    if (this.scaleVal) this.scaleVal.textContent = Math.round(this.zoom * 100) + '%';
  };

  P._uiRot = function () {
    if (this.rotVal) this.rotVal.textContent = Math.round(this.rot) + '°';
  };

  /* ---- Animation ---- */

  P._stop = function () { if (this._anim) { cancelAnimationFrame(this._anim); this._anim = null; } };

  P._snap = function () {
    var l = this._lim();
    var tx = clamp(this.px, -l.x, l.x), ty = clamp(this.py, -l.y, l.y);
    var tz = clamp(this.zoom, 1, MAX_ZOOM);
    if (Math.abs(this.px - tx) < 0.5 && Math.abs(this.py - ty) < 0.5 && Math.abs(this.zoom - tz) < 0.002) {
      this.px = tx; this.py = ty; this.zoom = tz;
      this._render(); this._ui(); this._res(); return;
    }
    this._animTo(tx, ty, tz);
  };

  P._animTo = function (tx, ty, tz) {
    var self = this;
    var sx = this.px, sy = this.py, sz = this.zoom, t0 = performance.now();
    this._stop();
    (function step(now) {
      var t = Math.min((now - t0) / SNAP_MS, 1), e = ease(t);
      self.px = sx + (tx - sx) * e;
      self.py = sy + (ty - sy) * e;
      self.zoom = sz + (tz - sz) * e;
      self._render(); self._ui();
      if (t < 1) self._anim = requestAnimationFrame(step);
      else { self._anim = null; self._res(); }
    })(performance.now());
  };

  /* ---- Reset ---- */

  P.reset = function (instant) {
    this.px = 0; this.py = 0; this.zoom = 1;
    this.rot = 0; this.q = 0;
    this.fh_ = false; this.fv_ = false;
    this._layout();
    if (instant) this._render(); else this._animTo(0, 0, 1);
    this._ui(); this._res();
    this.el.querySelectorAll('[data-gs-crop-flip-h],[data-gs-crop-flip-v]').forEach(function (b) {
      b.setAttribute('aria-pressed', 'false');
    });
  };

  /* ---- Resolution ---- */

  P._res = function () {
    if (!this.warn || !this.iw) return;
    var ds = this._ds();
    var pW = this.fw / ds / this.printW, pH = this.fh / ds / this.printH;
    this.warn.hidden = Math.min(pW, pH) >= MIN_PPI;
  };

  /* ---- Crop data ---- */

  P.getData = function () {
    if (!this.iw) return null;
    var ds = this._ds(), r = this._totalR(), rad = r * DEG;
    var co = Math.cos(rad), si = Math.sin(rad);
    var fhS = this.fh_ ? -1 : 1, fvS = this.fv_ ? -1 : 1;
    var rx = -this.px * fhS, ry = -this.py * fvS;
    var ux = rx * co + ry * si, uy = -rx * si + ry * co;
    return {
      x: Math.round(ux / ds + this.iw / 2),
      y: Math.round(uy / ds + this.ih / 2),
      w: Math.round(this.fw / ds),
      h: Math.round(this.fh / ds),
      zoom: Math.round(this.zoom * 100) / 100,
      rotation: Math.round(r * 100) / 100,
      flipH: this.fh_, flipV: this.fv_
    };
  };

  P.destroy = function () { this._stop(); if (this._ro) this._ro.disconnect(); };

  /* Export */
  window.GlowstoneCropper = Cropper;
  window.GlowstoneCropper.parseRatio = parseRatio;
  window.GlowstoneCropper.parseDims = parseDims;
})();
